import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { serviceSchema } from '../lib/structuredData';
import { Building2, Store, Home, Factory, CheckCircle2, ArrowRight } from 'lucide-react';

const Commercial = () => {
  const sectors = [
    { icon: Building2, title: 'Offices & Corporate', desc: 'Professional HVAC solutions for office environments' },
    { icon: Store, title: 'Retail & Shopping', desc: 'Climate control for retail spaces and shopping centers' },
    { icon: Home, title: 'Restaurants & Hospitality', desc: 'Specialized systems for food service and hotels' },
    { icon: Factory, title: 'Industrial & Warehouses', desc: 'Heavy-duty HVAC for industrial facilities' },
  ];

  const features = [
    'Bespoke Commercial Solutions',
    '24/7 Emergency Support',
    'Planned Maintenance Contracts',
    'Energy-Efficient Systems',
    'Minimal Disruption Installation',
    'F-Gas Compliant Services',
    'Multi-Site Coverage',
    'Competitive Commercial Rates',
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Commercial HVAC London"
        description="Commercial air conditioning installation, maintenance and emergency support for London offices, retail, hospitality and industrial sites. Planned maintenance contracts available."
        path="/commercial"
        jsonLd={serviceSchema(
          'Commercial HVAC Installation & Maintenance',
          'Bespoke commercial air conditioning, VRV/VRF and ducted system design for offices, retail, restaurants and warehouses across London.'
        )}
      />
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 14, 20, 0.55), rgba(10, 14, 20, 0.70)), url('/images/hero-commercial.jpg')`,
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <span className="inline-block text-xs bg-white/10 px-4 py-2 rounded-full text-[#FF8C00] font-bold border border-[#FF8C00]/30 tracking-wider mb-6">
            COMMERCIAL SERVICES
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
            Commercial <span className="text-[#FF8C00]">HVAC</span> Solutions
          </h1>
          <p className="text-gray-300 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto">
            Bespoke commercial HVAC solutions for offices, retail, restaurants and large commercial spaces across London.
          </p>
        </div>
      </section>

      {/* Commercial Features */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle2 size={24} className="text-[#FF8C00] flex-shrink-0 mt-0.5" />
                <span className="text-base font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors We Serve */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">SECTORS WE SERVE</p>
            <h2 className="text-4xl md:text-5xl font-bold">Industries We Support</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sectors.map((sector, idx) => {
              const Icon = sector.icon;
              return (
                <div key={idx} className="bg-[#0A0E14] border border-white/5 rounded-xl p-8 text-center hover:border-[#FF8C00]/50 transition-all group">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF8C00]/10 flex items-center justify-center group-hover:bg-[#FF8C00]/20 transition-colors">
                    <Icon size={32} className="text-[#FF8C00]" />
                  </div>
                  <h3 className="text-lg font-bold mb-3">{sector.title}</h3>
                  <p className="text-gray-400 text-sm">{sector.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Commercial */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">COMMERCIAL EXPERTISE</p>
              <h2 className="text-4xl font-bold mb-6">Trusted by London Businesses</h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                We understand that commercial properties have unique HVAC needs. Our team specializes in designing, installing, and maintaining systems that keep your business running smoothly.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#FF8C00] flex-shrink-0 mt-1" />
                  <span>Minimal disruption to your business operations</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#FF8C00] flex-shrink-0 mt-1" />
                  <span>Energy-efficient solutions to reduce running costs</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#FF8C00] flex-shrink-0 mt-1" />
                  <span>24/7 emergency support for critical breakdowns</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#FF8C00] flex-shrink-0 mt-1" />
                  <span>Planned maintenance contracts for peace of mind</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-white font-bold px-8 py-6 rounded-md text-base" data-testid="commercial-cta-quote">
                  Request Commercial Quote <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div 
              className="h-[500px] rounded-2xl bg-cover bg-center"
              style={{
                backgroundImage: `url('/images/hero-commercial.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Need a Commercial Solution?</h2>
          <p className="text-gray-400 text-lg mb-8">Speak directly with our HVAC specialists for expert advice.</p>
          <Link to="/contact">
            <Button className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-white font-bold px-10 py-6 rounded-md text-base" data-testid="commercial-cta-engineer">
              Speak To An Engineer <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Commercial;
